import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminSidebar, ShellContext } from '../ui';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useUIStore } from '../store/uiStore';
import { fetchDevices } from '../api/devices';

const ROLE_LABEL: Record<string, string> = {
  ADMIN: '본사 관리자',
  OPERATOR: '지역 담당자',
  VIEWER: '뷰어',
};

/**
 * App shell: GroVision M3 sidebar + main column. Applies the light/dark theme
 * class and exposes global chrome (collapse toggle, alert badge) via context.
 */
export function Shell({ children }: { children: React.ReactNode }): React.ReactElement {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const dark = useThemeStore((s) => s.dark);
  const toggleDark = useThemeStore((s) => s.toggleDark);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const isAdmin = user?.role === 'ADMIN';

  // Alert badge: count devices currently in warning/error.
  const { data: devices } = useQuery({ queryKey: ['devices'], queryFn: fetchDevices, refetchInterval: 60_000 });
  const warnCount = (devices ?? []).filter((d) => d.status === 'WARNING' || d.status === 'ERROR').length;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <ShellContext.Provider value={{ toggleCollapse: toggleSidebar, alertCount: warnCount }}>
      <div
        className={dark ? 'md-dark' : 'md-light'}
        style={{
          height: '100vh',
          display: 'flex',
          overflow: 'hidden',
          background: 'var(--md-sys-color-surface-container)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        <AdminSidebar
          collapsed={collapsed}
          dark={dark}
          onToggleDark={toggleDark}
          onLogout={handleLogout}
          isAdmin={isAdmin}
          userName={user?.name ?? '관리자'}
          userEmail={user?.email ?? ''}
          roleLabel={user ? (ROLE_LABEL[user.role] ?? user.role) : '관리자'}
          badges={{ alerts: warnCount }}
        />
        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            background: 'var(--md-sys-color-surface)',
            overflow: 'hidden',
          }}
        >
          {children}
        </main>
      </div>
    </ShellContext.Provider>
  );
}
